import {
  addOrRemove,
  deepEqual,
  equals as unimpairedEquals,
} from '@unimpaired/utils'
import { any, prop, equals } from 'ramda'
import React, { useState, useEffect, useRef } from 'react'
import {
  useController,
  useForm,
  UseFormReturn,
  useWatch,
} from 'react-hook-form'
import { OptionsList } from './components/OptionsList'
import { SelectOptionButton } from './components/SelectOptionButton'
import { SelectOptions } from './components/SelectOptions'
import { getChildrenProps } from './select.helpers'
import { Option } from './components/Option/Option'
import { twMerge } from 'tailwind-merge'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { InputError } from '../Input/InputError'
import { usePrevious } from '../../../hooks'
import { ShowSkeletonOptions } from './components/ShowSkeletonOptions'
import { LoadMoreSelections } from './components/LoadMoreSelections'
import { InputLabel } from '../Input'

export type ListQueryProps = {
  loading: boolean
  count?: number
  take: number
  fetchNextPage: () => void
  isFetching: boolean
  canFetchNextPage?: boolean
  searchState: readonly [string, (s: string) => void]
}

export type SelectVariant =
  | 'form-field'
  | 'prefixed-dropdown'
  | 'switch-group'
  | 'pill-group'

export interface SelectProps<
  T extends unknown = string,
  R extends ListQueryProps = ListQueryProps,
> {
  name: string
  label: string
  form?: UseFormReturn<any>
  onSelect?: (selections: this['multiple'] extends true ? T[] : T) => void
  children: React.FunctionComponentElement<
    Selection<this['multiple'] extends true ? T[] : T>
  >[]
  onSearch?: (searchTerm: string) => void
  search?: boolean
  icon?: IconDefinition
  multiple?: boolean
  defaultValues?: T[] | T
  required?: boolean
  disabled?: boolean
  noLabel?: boolean
  placeholder?: string
  ['data-testid']?: string
  className?: string
  dropDownClasses?: string
  closeOnFirstSelection?: boolean
  /** This controls the classes surrounding the button itself, such as the border and font */
  buttonClasses?: string
  labelClasses?: string
  appendElementToDropDown?: React.ReactNode
  keepMenuOnAppendedElementClick?: boolean
  variant?: SelectVariant
  listQueryProps?: R
  customEqualityCheck?: <F extends T>(a: F, b: F) => boolean
  /** Programmatic accessible name for the trigger. Defaults to `label` so
   *  every Select exposes a name to AT / `getByRole('combobox', { name })`
   *  even when the visible label is hidden (`noLabel`) or rendered apart. */
  ['aria-label']?: string
  /** Id(s) of the element(s) that label the trigger. Takes precedence over
   *  `aria-label` per ARIA when both are present. */
  ['aria-labelledby']?: string
}

export type Selection<T extends any = string> = {
  label: string
  value: T
  nested?: boolean
}

export function Select<
  T extends any = string,
  R extends ListQueryProps = ListQueryProps,
>(props: SelectProps<T, R>) {
  const [selectOpen, setSelectOpen] = useState(false)
  const [selections, setSelections] = useState<Selection<T>[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [noSelectionsMade, setNoSelectionsMade] = useState(true)

  const validators = {
    required: props.required,
    validate: (value: string | string[]) => {
      if (!props.required) return true
      if (typeof value === 'undefined') return 'Fill out this field'
      if (Array.isArray(value)) {
        const [first] = value
        if (!first) return 'Fill out this field'
      }
      return true
    },
  }

  // This form is here for when the select is used without a form given
  // If we do not a provide a formcontrol to the useController hook, it will error out.
  const form: UseFormReturn<any> = props.form ? props.form : useForm()
  const { field, fieldState } = useController({
    name: props.name,
    control: form.control,
    rules: validators,
    defaultValue: props.defaultValues,
  })

  useEffect(() => {
    if (props.onSearch) props.onSearch(searchTerm)
    if (props.listQueryProps) {
      props.listQueryProps.searchState[1](searchTerm)
    }
  }, [searchTerm])

  const formValue = useWatch({ control: form.control, name: props.name })

  const previousOpen = usePrevious(selectOpen)
  useEffect(() => {
    if (!props.required) return
    if (previousOpen && !selectOpen) form.trigger(props.name)
  }, [selectOpen])

  // ramda equals breaks for dates; use unimpairedEquals in dev to catch issues, equals in prod until Select is fixed
  const equalityCheck =
    props.customEqualityCheck ||
    (import.meta.env.DEV ? unimpairedEquals : equals)

  // This effect is what causes the update when the parent form calls form.setValue('name', ...)
  // This effect also handles asynchronously setting the initial value of the select field
  // NOTE:  This might have conflicts with the effect below this one.  I made this change to fix a bug.
  useEffect(() => {
    // If we are using a multi select, we only want to use this effect to set the initial value
    // if (props.multiple && selections.length > 0) return

    const childProps = getChildrenProps<Selection<T>>(props.children)
    if (!formValue) return

    if (!props.multiple) {
      // If the select fields selections already includes what is in the parent form, stop
      if (deepEqual(formValue, prop('value', selections[0]))) return
      setSelections(
        childProps.filter((option) => equals(option.value, formValue)),
      )
    }

    if (deepEqual(formValue, selections.map(prop('value')))) return // Only run if selections and form value aren't equivalent
    const selected = childProps.filter(({ value }) => {
      return any(
        (s) => equalityCheck(s, value),
        Array.isArray(formValue) ? formValue : [formValue],
      )
    })
    setSelections(selected.map(({ value, label }) => ({ value, label })))
  }, [formValue, props.children])

  // Set default values
  useEffect(() => {
    const defaultValues = !props.defaultValues
      ? []
      : Array.isArray(props?.defaultValues)
      ? props.defaultValues
      : [props.defaultValues]
    // if there are no default values, stop
    if (defaultValues.length === 0) return
    // if selection has already been set, stop
    if (selections.length > 0) return

    const childProps = getChildrenProps<Selection<T>>(props.children)
    const defaultSelections = childProps.filter(({ value }) =>
      any(deepEqual(value), defaultValues),
    )
    // if none of the selections match the default, return
    if (defaultSelections.length === 0) return

    setSelections(defaultSelections)

    // We need to set the form to actually contain the date passed in in the
    // defaults
    // If defaults was passed in as an array but this is a single ...
    if (
      Array.isArray(props.defaultValues) &&
      !props.multiple &&
      props.defaultValues[0]
    )
      field.onChange(props.defaultValues[0])
    // otherwise the value is fine as is
    else field.onChange(props.defaultValues)
  }, [props.defaultValues, props.children])

  const singleSelect = (selection: Selection<T>) => {
    setSelectOpen(false)
    setSelections(([prevSelection]) => {
      if (equals(selection?.value, prevSelection?.value)) {
        // If this form is required, we do not want it to ever be empty
        if (props.required) return [prevSelection]

        if (props.onSelect) props.onSelect(undefined as T)
        field.onChange(undefined)
        return []
      }

      if (props.onSelect) props.onSelect(selection.value)
      field.onChange(selection.value)
      return [selection]
    })
    setNoSelectionsMade(false)
    if (fieldState.invalid) form.trigger()
  }

  const multiSelect = (selection: Selection<T>) => {
    setSelections((prevSelections) => {
      const newSelections = addOrRemove(selection, prevSelections)
      const values = newSelections.map(({ value }) => value)
      // typescript can't infer that this is a multi select from this spot
      // @ts-expect-error
      if (props.onSelect) props.onSelect(values)
      field.onChange(values)
      return newSelections
    })

    if (props.closeOnFirstSelection && noSelectionsMade) {
      setSelectOpen(false)
    }

    setNoSelectionsMade(false)
    if (fieldState.invalid) form.trigger()
  }

  const handleSelect = props.multiple ? multiSelect : singleSelect

  const triggerRef = useRef<HTMLButtonElement>(null)

  const variant = props.variant || 'form-field'

  return (
    <div
      className={twMerge(
        'flex-col w-full',
        props.variant === 'form-field' && 'mt-6',
        props.className,
      )}>
      {!props.variant && (
        <InputLabel
          label={props.label}
          noLabel={props.noLabel}
          required={props.required}
          className={props.labelClasses}
        />
      )}

      <div
        className={twMerge(
          'relative w-full',
          props.variant === 'prefixed-dropdown' && 'w-fit',
        )}>
        <SelectOptionButton
          variant={variant}
          fieldState={fieldState}
          className={props.buttonClasses}
          ref={triggerRef}
          icon={props.icon}
          disabled={props.disabled}
          handleSelect={handleSelect}
          label={props.label}
          // Forward an explicit aria-label only — never default to props.label,
          // which would override the trigger's content-based name app-wide (a
          // Select queried by its placeholder/value loses that name). Selects
          // that need a programmatic name pass aria-label explicitly.
          aria-label={props['aria-labelledby'] ? undefined : props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          optionsVisible={selectOpen}
          setOptionsVisible={() => {
            setSelectOpen((v) => {
              if (v) form.trigger(props.name)
              return !v
            })
          }}
          selections={selections}
          multiple={props.multiple}
          placeholder={props.placeholder}
          noLabel={props.noLabel}
        />

        {/* In order to support react-hook-form, we bind the select value to this input */}
        <input
          className='hidden sr-only'
          ref={field.ref}
          id={props.label}
          placeholder={props.label}
          value={selections.join(', ') || ''}
          name={field.name}
          readOnly
        />
        {/* Show Error if Required */}
        <InputError
          fieldValue={field.value}
          validators={validators}
          error={form.formState.errors[props.name]}
        />
      </div>

      {(props.variant === 'switch-group' || props.variant === 'pill-group') && (
        <div
          className={twMerge(
            'mt-4 grid grid-cols-1 gap-y-6 sm:gap-x-4 sm:grid-cols-2 md:grid-cols-3 ',
            props.className,
          )}>
          <OptionsList
            multiple={props.multiple}
            variant={props.variant}
            setSelectOpen={setSelectOpen}
            handleSelect={handleSelect}
            listQueryProps={props.listQueryProps}
            searchTerm={searchTerm}
            selections={selections}>
            {props.children}
          </OptionsList>
        </div>
      )}

      <SelectOptions
        isVisible={selectOpen}
        className={twMerge(
          'flex flex-col items-stretch',
          props.dropDownClasses,
        )}
        search={props.search || !!props?.listQueryProps?.searchState}
        setVisible={setSelectOpen}
        setSearchTerm={setSearchTerm}
        label={props.label}
        listQueryProps={props.listQueryProps}
        triggerRef={triggerRef}
        selections={selections}>
        <div
          onClick={() =>
            !props.keepMenuOnAppendedElementClick && setSelectOpen(false)
          }>
          {props.appendElementToDropDown && props.appendElementToDropDown}
        </div>
        <OptionsList
          multiple={props.multiple}
          setSelectOpen={setSelectOpen}
          handleSelect={handleSelect}
          listQueryProps={props.listQueryProps}
          searchTerm={searchTerm}
          selections={selections}>
          {props.children}
        </OptionsList>
        {props.listQueryProps && (
          <ShowSkeletonOptions listQueryProps={props.listQueryProps} />
        )}
        {props.listQueryProps && (
          <LoadMoreSelections listQueryProps={props.listQueryProps} />
        )}
      </SelectOptions>
    </div>
  )
}

Select.Option = Option
